import * as tf from '@tensorflow/tfjs'

// YOLOv8 (TF.js graph model) object detector. Returns the SAME shape as the
// coco-ssd wrapper — [{ class, score, bbox:[x,y,w,h] }] in original pixels — so
// it drops into detectionProvider with nothing downstream changing.
//
// SWAP MODEL SIZE: drop a converted folder under public/models (e.g.
// yolov8s_web_model) and point MODEL_URL at its model.json. Input is 640x640,
// output [1, 84, 8400] (4 bbox + 80 COCO classes) for every yolov8 size.
const MODEL_URL =
    (process.env.PUBLIC_URL || '') + '/models/yolov8n_web_model/model.json'
const INPUT_SIZE = 640
const NUM_CLASS = 80
const SCORE_THRESHOLD = 0.3
const IOU_THRESHOLD = 0.45

// prettier-ignore
export const COCO_CLASSES = [
    'person','bicycle','car','motorcycle','airplane','bus','train','truck','boat',
    'traffic light','fire hydrant','stop sign','parking meter','bench','bird','cat',
    'dog','horse','sheep','cow','elephant','bear','zebra','giraffe','backpack',
    'umbrella','handbag','tie','suitcase','frisbee','skis','snowboard','sports ball',
    'kite','baseball bat','baseball glove','skateboard','surfboard','tennis racket',
    'bottle','wine glass','cup','fork','knife','spoon','bowl','banana','apple',
    'sandwich','orange','broccoli','carrot','hot dog','pizza','donut','cake','chair',
    'couch','potted plant','bed','dining table','toilet','tv','laptop','mouse',
    'remote','keyboard','cell phone','microwave','oven','toaster','sink',
    'refrigerator','book','clock','vase','scissors','teddy bear','hair drier',
    'toothbrush',
]

let modelPromise = null

export function loadYolo() {
    if (!modelPromise) {
        modelPromise = tf.loadGraphModel(MODEL_URL).then(async (model) => {
            // Warm up so the first real frame isn't slow.
            const warm = tf.zeros([1, INPUT_SIZE, INPUT_SIZE, 3])
            const out = model.execute(warm)
            tf.dispose([warm, out])
            return model
        })
    }
    return modelPromise
}

// Pad the frame to a square (top-left origin) then resize to 640 — keeps aspect
// ratio, so boxes map back with a single scale and no offset.
function preprocess(video) {
    let scale = 1
    const input = tf.tidy(() => {
        const img = tf.browser.fromPixels(video)
        const [h, w] = img.shape.slice(0, 2)
        const maxSize = Math.max(w, h)
        scale = maxSize / INPUT_SIZE
        const padded = img.pad([
            [0, maxSize - h],
            [0, maxSize - w],
            [0, 0],
        ])
        return tf.image
            .resizeBilinear(padded, [INPUT_SIZE, INPUT_SIZE])
            .div(255)
            .expandDims(0)
    })
    return { input, scale }
}

// Run detection on a video/canvas element. Returns up to `max` detections as
// { class:<cocoName>, score, bbox:[x,y,w,h] } in the source's pixel space.
export async function yoloDetect(model, video, max = 20) {
    if (!model || !video) return []
    const { input, scale } = preprocess(video)

    const res = model.execute(input) // [1, 84, 8400]
    input.dispose()

    // [1,84,8400] -> [8400,84]; split bbox (0..3) and class scores (4..83).
    const { boxes, scores, classes } = tf.tidy(() => {
        const t = res.transpose([0, 2, 1]).squeeze([0]) // [8400,84]
        const cx = t.slice([0, 0], [-1, 1])
        const cy = t.slice([0, 1], [-1, 1])
        const w = t.slice([0, 2], [-1, 1])
        const h = t.slice([0, 3], [-1, 1])
        const x1 = cx.sub(w.div(2))
        const y1 = cy.sub(h.div(2))
        // nonMaxSuppression expects [y1,x1,y2,x2].
        const boxes4 = tf.concat([y1, x1, y1.add(h), x1.add(w)], 1)
        const clsScores = t.slice([0, 4], [-1, NUM_CLASS]) // [8400,80]
        return {
            boxes: boxes4,
            scores: clsScores.max(1),
            classes: clsScores.argMax(1),
        }
    })
    res.dispose()

    let keep
    try {
        keep = await tf.image.nonMaxSuppressionAsync(
            boxes,
            scores,
            max,
            IOU_THRESHOLD,
            SCORE_THRESHOLD
        )
    } catch (e) {
        tf.dispose([boxes, scores, classes])
        return []
    }

    const idx = keep.dataSync()
    const boxData = boxes.gather(keep).dataSync() // [n*4] y1,x1,y2,x2 (640 space)
    const scoreData = scores.gather(keep).dataSync()
    const classData = classes.gather(keep).dataSync()
    tf.dispose([boxes, scores, classes, keep])

    const out = []
    for (let i = 0; i < idx.length; i++) {
        const y1 = boxData[i * 4] * scale
        const x1 = boxData[i * 4 + 1] * scale
        const y2 = boxData[i * 4 + 2] * scale
        const x2 = boxData[i * 4 + 3] * scale
        out.push({
            class: COCO_CLASSES[classData[i]] || 'object',
            score: scoreData[i],
            bbox: [x1, y1, x2 - x1, y2 - y1],
        })
    }
    return out
}
